import * as yup from "yup";

import {
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  PersonOutlined as PersonOutlinedIcon,
  StarOutlined as StarOutlinedIcon,
  TrendingUpOutlined as TrendingUpOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
// import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Controller, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sidebar from '../../components/Sidebar';
import TokenExpiredModal from '../../components/TokenExpiredModal';
import UploadPDF from '../../components/UploadCVExtractDataWithAiApp';
import api from '../../api/axiosInstance';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import pieChartImage from "../../images/MainChart.png";
import { primaryRoles } from '../../config';
import {
  useAddNewCVMutation,
} from '../../lib/cvsSlice';
import { useNavigate } from 'react-router';
import { useProfile } from '../../providers/ProfileProvider';
import { yupResolver } from '@hookform/resolvers/yup';

// Validation schema
const cvHeaderSchema = yup.object().shape({
  mainRole: yup.string().required("CV Role is required"),
});

// Mock data
const platformStats = {
  registeredUsers: 127543,
  registeredUsersGrowth: 12,
  cvsCreatedToday: 1247,
  successRate: 94,
};

const userProgress = {
  cvRole: "Marketing Manager",
  completionPercentage: 75,
};

const HEADER_HEIGHT = 65;

const gettingStartedTips = [
  { id: 1, text: "Start with a professional template", completed: true },
  { id: 2, text: "Use action verbs to describe your experience", completed: true },
  { id: 3, text: "Keep your CV to 1-2 pages maximum", completed: true },
];

const roleDistributionData = [
  { name: 'Mobile Developer', color: '#8979FF' },
  { name: 'UX/UI Designer',  color: '#FF928A' },
  { name: 'Web Developer',  color: '#3CC3DF' },
  { name: 'Scrum Master',  color: '#FFAE4C' },
  { name: 'Project Manager',  color: '#537FF1' },
];

// Icon Container Component
const IconContainer = ({ children, sx = {} }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      backgroundColor: '#EAE7E6',
      borderRadius: '8px',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mr: '10px',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Custom Card Components
const WelcomeCard = ({ title, description, onCreateClick, width }) => (
  <Paper
    elevation={2}
    sx={{
      width: width || '551px',
      height: '152px',
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      borderRadius: '8px',
      gap: '8px',
    }}
  >
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#E84610',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
          }}
        >
          <AddIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.875rem' }}>
        {description}
      </Typography>
    </Box>
    
    <Button
      fullWidth
      variant="contained"
      onClick={onCreateClick}
      startIcon={<AddIcon />}
      sx={{
        background: '#E84610',
        color: 'white',
        py: 1,
        borderRadius: '8px',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        '&:hover': {
          background: '#E84610',
        },
      }}
    >
      Create CV
    </Button>
  </Paper>
);

const ProgressCard = ({ cvRole, completionPercentage, width }) => (
  <Paper
    elevation={2}
    sx={{
      width: width || '551px',
      height: '152px',
      p: 2, // 16px padding
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      borderRadius: '8px',
      gap: '8px',
    }}
  >
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: '#6c5ce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: .5,
          }}
        >
          <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          Complete Your CV
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.875rem' }}>
        Your "{cvRole}" CV is {completionPercentage}% complete
      </Typography>
      <LinearProgress
        variant="determinate"
        value={completionPercentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: '#f0f0f0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#333',
            borderRadius: 3,
          },
        }}
      />
    </Box>
    
    <Button
      fullWidth
      variant="outlined"
      startIcon={<EditIcon />}
      sx={{
        color: '#666',
        borderColor: '#ddd',
        py: 1,
        borderRadius: '8px',
        fontSize: '0.875rem',
        '&:hover': {
          borderColor: '#bbb',
          backgroundColor: '#f8f9fa',
        },
      }}
    >
      Complete
    </Button>
  </Paper>
);

const TipsCard = ({ tips, width }) => (
  <Paper
    elevation={2}
    sx={{
      width: width || '269px',
      height: '290px',
      p: 2, // 16px padding
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '8px',
      gap: '16px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconContainer>
        <StarOutlinedIcon sx={{ color: '#463C3B', fontSize: 24 }} />
      </IconContainer>
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
        Getting Started Tips
      </Typography>
    </Box>
    
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      {tips.slice(0, 3).map((tip) => (
        <Box key={tip.id} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
          <Checkbox
            checked={tip.completed}
            size="small"
            sx={{
              color: '#463C3B',
              '&.Mui-checked': {
                color: '#463C3B',
              },
              p: 0.25,
            }}
          />
          <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.8rem', lineHeight: 1.4 }}>
            {tip.text}
          </Typography>
        </Box>
      ))}
    </Box>

    <Link
      href="#"
      sx={{
        display: 'flex',
        alignItems: 'center',
        color: '#E84610',
        textDecoration: 'none',
        fontSize: '0.8rem',
        fontWeight: 'medium',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      View complete guide
      <ArrowForwardIcon sx={{ ml: 0.5, fontSize: 14 }} />
    </Link>
  </Paper>
);

const StatsCard = ({ stats, width }) => (
  <Paper
    elevation={2}
    sx={{
      width: width || '269px',
      height: '290px',
      p: 2, // 16px padding
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '8px',
      gap: '16px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconContainer>
        <TrendingUpOutlinedIcon sx={{ color: '#463C3B', fontSize: 24 }} />
      </IconContainer>
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
        Platform Stats
      </Typography>
    </Box>
    
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          Registered Users
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            {stats.registeredUsers.toLocaleString()}
          </Typography>
          <Typography variant="caption" color='#463C3B' sx={{ fontSize: '0.7rem' }}>
            +{stats.registeredUsersGrowth}% this month
          </Typography>
        </Box>
      </Box>
      
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          CVs Created Today
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          {stats.cvsCreatedToday.toLocaleString()}
        </Typography>
      </Box>
      
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          Success Rate
        </Typography>
        <Typography variant="h6" fontWeight="bold" color='#463C3B' sx={{ fontSize: '1.1rem' }}>
          {stats.successRate}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          Users who got interviews
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const ChartCard = ({ data, width }) => (
  <Paper
    elevation={2}
    sx={{
      width: width || '564px',
      height: '290px',
      pt: 3,
      pr: 2,  
      pb: 1, 
      pl: 2, 
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '8px',
      gap: '8px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <IconContainer>
        <PersonOutlinedIcon sx={{ color: '#463C3B', fontSize: 24 }} />
      </IconContainer>
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
        Top User Roles Distribution
      </Typography>
    </Box>
    
    {/* <Box sx={{ flex: 1, display: 'flex' }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box> */}
      <Box sx={{ flex: 1, display: 'flex' }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src={pieChartImage} 
          alt="User Roles Distribution Pie Chart" 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%', 
            objectFit: 'contain' 
          }} 
        />
      </Box>
      
      <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '140px' }}>
        {data.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: entry.color,
                mr: 1,
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {entry.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Paper>
);

const HomeDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const profile = useProfile();

  // Dialog and CV management state
  const [open, setOpen] = useState(false);
  const [addNewCV] = useAddNewCVMutation();
  const [allCvs, setAllCvs] = useState([]);
  const [allCvTitles, setAllCvTitles] = useState([]);
  const [copyCvId, setCopyCvId] = useState(null);
  const [tokenExpiredModalOpen, setTokenExpiredModalOpen] = useState(false);
  const [createFromPersonal, setCreateFromPersonal] = useState(false);

  // Form setup
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvHeaderSchema),
    defaultValues: {
      mainRole: "",
    },
  });

  // Fetch CV data
  useEffect(() => {
    const fetchCvData = async () => {
      try {
        const { data: response } = await api.get(
          `/cv?owner=${profile._id}`,
        );
        setAllCvs(response);
        setAllCvTitles(response.map(cvs => cvs.mainRole));
      } catch (error) {
        console.error(error.message);
        if (error.response && error.response.status === 401){
          setTokenExpiredModalOpen(true);
        }
      }
    };

    if (profile?._id) {
      fetchCvData();
    }
  }, [profile]);

  // Profile check
  useEffect(() => {
    if (profile && !profile.secondaryEmail) {
      navigate('/onboarding');
    }
  }, [profile, navigate]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setCopyCvId(null);
  };

  // Form submission
  const submitResults = async (formData) => {
    const { mainRole } = formData;

    let cvData = {};

    if (copyCvId) {
      try {
        const response = await api.get(`cv/${copyCvId}`);

        cvData = {
          languages: response.data.languages || [{}],
          educations: response.data.educations || [{}],
          skills: response.data.skills || [{}],
          achievements: response.data.achievements || [],
          certifications: response.data.certifications || [{}],
          projects: response.data.projects || [{}],
          courses: response.data.courses || [{}],
          gitRepo: response.data.gitRepo || "",
          portfolio: response.data.portfolio || "",
          summary: response.data.summary || "",
          techSkills: response.data.techSkills || [{}],
          techExperiences: response.data.techExperiences || [{}],
        };

      } catch (error) {
        console.error(error);
      }
    }

    const requestData = {
      mainRole,
      ...(copyCvId ? cvData : {}),
      ...formData,
    };

    try {
      const { data: responseData } = await addNewCV(requestData);
      setOpen(false);
      reset();
      setCopyCvId(null);
      navigate(`/cvdetails/${responseData._id}`);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleCreateCV = () => {
    handleClickOpen();
  };

  return (
    <div>
      <TokenExpiredModal
        isOpen={tokenExpiredModalOpen}
        onClose={() => setTokenExpiredModalOpen(false)}
      />

      {/* Sidebar */}
      <Sidebar currentPath="/" />

      {/* Main Content Area */}
      <div 
        style={{
          position: 'fixed',
          top: '65px',
          left: '258px',
          width: '85vw',
          height: '95vh',
          overflow: 'auto',
          padding: '16px 24px',
          boxSizing: 'border-box',
          background: '#EAE7E6'
        }}
      >
        {/* Header */ }
        {/*Replace with dynamic user name*/}
        <div style={{ marginBottom: '32px' }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ 
              mb: 1,
              fontSize: '24px',
              color: '#000000',
              lineHeight: '2rem',
              fontFamily: 'Readex Pro',
            }}
          >
            Hello Jane, Welcome! 
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 'normal',
              fontSize: '1rem',
            }}
          >
            Ready to create your professional CV? Here's everything you need to get started.
          </Typography>
        </div>

  
        <div style={{ position: 'relative' }}>
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: 'calc((100vw - 346px) / 2)', 
            height: '152px' 
          }}>
            <WelcomeCard
              title="Create New CV"
              description="Start building a new CV from scratch"
              onCreateClick={handleCreateCV}
              width="calc((100vw - 346px) / 2)"
            />
          </div>

          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 'calc((100vw - 346px) / 2 + 16px)',
            width: 'calc((100vw - 346px) / 2)',
            height: '152px' 
          }}>
            <ProgressCard
              cvRole={userProgress.cvRole}
              completionPercentage={userProgress.completionPercentage}
              width="calc((100vw - 346px) / 2)"
            />
          </div>

          {/* Bottom Row - 3 cards with specified percentages */}
          <div style={{ 
            position: 'absolute', 
            top: '168px', // 152 + 16px gap
            left: 0, 
            width: 'calc((100vw - 275px - 48px) * 0.2406)', 
            height: '290px' 
          }}>
            <TipsCard 
              tips={gettingStartedTips} 
              width="calc((100vw - 275px - 48px) * 0.2406)"
            />
          </div>

          <div style={{ 
            position: 'absolute', 
            top: '168px', 
            left: 'calc((100vw - 275px - 48px) * 0.2406 + 16px)', // First card width plus gap
            width: 'calc((100vw - 275px - 48px) * 0.2406)', // 24.06% of available width
            height: '290px' 
          }}>
            <StatsCard 
              stats={platformStats}
              width="calc((100vw - 275px - 48px) * 0.2406)"
            />
          </div>

          <div style={{ 
            position: 'absolute', 
            top: '168px', 
            left: 'calc((100vw - 275px - 48px) * 0.4812 + 32px)',
            width: 'calc((100vw - 275px - 48px) * 0.5045)', 
            height: '290px' 
          }}>
            <ChartCard 
              data={roleDistributionData}
              width="calc((100vw - 275px - 48px) * 0.5045)"
            />
          </div>
        </div>
      </div>

      {/* Create CV Dialog */}
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faFile} />
            Create New CV
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <form onSubmit={handleSubmit(submitResults)} id="AddNewCVForm" autoComplete="off">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="mainRole"
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Select role for CV"
                    fullWidth
                    error={!!errors.mainRole}
                    helperText={errors?.mainRole ? errors.mainRole?.message : ""}
                  >
                    {primaryRoles.map((role) => (
                      <MenuItem value={role} key={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={copyCvId !== null}
                    onChange={() => setCopyCvId(copyCvId !== null ? null : "")}
                    name="copyCV"
                    color="primary"
                  />
                }
                label="Copy from existing CV"
              />

              {copyCvId !== null && (
                <FormControl fullWidth>
                  <FormHelperText>Select CV to copy</FormHelperText>
                  <Select
                    value={copyCvId}
                    onChange={(e) => setCopyCvId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select CV to copy</em>
                    </MenuItem>
                    {allCvs.map((cvs) => (
                      <MenuItem value={cvs._id} key={cvs._id}>
                        {cvs.mainRole}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={createFromPersonal}
                    onChange={(event) => setCreateFromPersonal(event.target.checked)}
                    name="createFromPersonal"
                    color="primary"
                  />
                }
                label="Create from Personal CV (PDF)"
              />

              {createFromPersonal && <UploadPDF handleClose={handleClose} />}
            </Box>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button form="AddNewCVForm" type="submit" color="primary" variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HomeDashboard;